import Workflow, { ActionStatus, WorkflowAction } from 'models/Workflow';

import { PortalSectionField, portalSectionFields, REFERRAL_FIELD_SECTIONS, VALUE_NAMES } from '../constants';

import { ACTION_KINDS } from './constants';

/**
 * The by-property surface's one derivation (OLIO-3022): a run in, a state per property out.
 * Everything the slideout renders — the value, its validity mark, whether it can be typed into,
 * and where an edit posts — comes off this map, so no component re-derives any of it.
 *
 * Two things here are corrections to the plan, both established against the engine:
 *
 *  1. **The read is three-source, not `workflow.valueFor(name)`.** A value a script captured
 *     (extension-driven or headless) is STAGED on the outstanding action's `outputs` and is not a
 *     committed value at all — `record_outputs` deliberately publishes nothing. The
 *     extension-return flow, which is the whole review-and-correct moment, reads entirely off
 *     staged outputs, so a resolver that consulted `values[]` alone would show an empty form over
 *     a full capture.
 *
 *  2. **Editability is a portal rule, not an engine derivation.** No portal action declares a
 *     demographic as a required input — retrieval requires `source_system` and produces
 *     `attachments` + `facilities` — so "editable if an outstanding action declares it" would
 *     make every demographic permanently read-only. The rule that matches the engine is: while
 *     retrieval is outstanding, the portal collect registry's fields are what a person may fill,
 *     because retrieval's outputs are free-form and that is exactly how the manual retrieval form
 *     has always written them.
 *
 * A value nothing is currently asking for is STILL editable, and posts as a plain workflow value
 * rather than through an action. That is not a new engine capability: `Workflow::Update` writes
 * incoming `values` at MANUAL rank, and `WriteValues#wins?` is `rank >= winner.rank` with manual
 * the highest — so a person's correction beats an empty slot, a machine capture, and an earlier
 * manual entry alike. `EditInboundReferralPage` has been writing referral demographics through
 * exactly this path all along; the slideout just uses it too.
 *
 * This is what makes the extension-return flow worth having (a bad capture can be FIXED, not only
 * filled), and it is why a flagged `DisposePortalReferral` is no longer a dead end — its ⚠ rows
 * are editable like any other, so the footer can enable.
 */

export type FieldProvenance = 'staged' | 'committed' | 'absent';

// Where an edit to this field posts. Onto one action's outputs while something is asking for it —
// the output name is not always the field's name, since a name part writes through its composite
// and a facility pick writes as the mapping action's `facilityResolutions` — or as a plain
// workflow value when nothing is.
export type FieldTarget =
  | { kind: 'actionOutputs'; actionId: string; outputName: string }
  | { kind: 'workflowValues'; valueName: string };

export interface FieldState {
  name: string;
  value: unknown;
  provenance: FieldProvenance;
  // The validity mark, presence-based: green means PRESENT, not correct. A botched extraction
  // (`Abigail,Smith` in First Name) is present and marks valid — anything stronger is a
  // validation layer this surface deliberately does not have.
  present: boolean;
  editable: boolean;
  target: FieldTarget | null;
}

// The mapped result of the facility section. `facilities` is what the run COLLECTED (portal
// strings, or picks); this is what those resolved to, and it is what disposition addresses the
// referral with — so it, not the collected list, is what "the facility is settled" means.
export const MAPPED_FACILITIES_VALUE = 'facility_group_ids';

// The facility section's own output name on the mapping action. Mirrors the api's
// MapPortalFacilities::RESOLUTIONS_INPUT; camelCase on the wire, snake-cased by the middleware.
export const FACILITY_RESOLUTIONS_OUTPUT = 'facilityResolutions';

// Retrieval is addressable while it is waiting on a person AND while it has errored — a failed
// automated retrieval is recovered by a person entering what it could not, which is the same
// gesture against the same action (`detailWhenErrored`).
const RETRIEVAL_OUTSTANDING = [ActionStatus.ACTION_REQUIRED, ActionStatus.ERRORED];

export function outstandingRetrieval(workflow: Workflow): WorkflowAction | undefined {
  return workflow.actions.find(
    (action) => action.kind === ACTION_KINDS.retrieve && RETRIEVAL_OUTSTANDING.includes(action.status)
  );
}

export function outstandingFacilityMapping(workflow: Workflow): WorkflowAction | undefined {
  return workflow.actions.find((action) => action.kind === ACTION_KINDS.mapFacilities && action.isActionRequired);
}

// Present means: there is something there. An empty array is a value the engine calls present
// (only `nil` is absent to it) and a person calls empty — `facilities: []` is exactly that case,
// and it is the one the mapping step exists to ask about.
export function isPresent(value: unknown): boolean {
  if (value === undefined || value === null) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'string') return value.trim().length > 0;
  return true;
}

// The names a flagged action said it could not derive. These mark ⚠ even when a row exists, and
// they are the only field-level signal the API carries — there is no per-field validity anywhere
// else in the payload.
function unobtainableNames(workflow: Workflow): Set<string> {
  return new Set(workflow.actions.filter((action) => action.isFlagged).flatMap((action) => action.unobtainableValues));
}

// A staged capture for one row. Read in the shape a script actually sends: the value name itself,
// then the composite it belongs to.
//
// A single-name capture (`{full}`, or `patient_full_name`) has no first/last split until the api
// normalizes it, so it reads onto the FIRST row rather than being dropped — the same "present,
// not correct" stance the marks take. Showing an unsplit name is honest; hiding a capture the
// portal made is not.
function stagedValue(action: WorkflowAction | undefined, field: PortalSectionField): unknown {
  if (!action) return undefined;

  const direct = action.outputFor(field.name);
  if (direct !== undefined) return direct;
  if (!field.composite) return undefined;

  const composite = action.outputFor(field.composite.name);
  const parts = composite && typeof composite === 'object' ? (composite as Record<string, unknown>) : {};
  const single = parts.full ?? action.outputFor(VALUE_NAMES.PATIENT_FULL_NAME);
  if (isPresent(parts[field.composite.part])) return parts[field.composite.part];
  return field.composite.part === 'first' ? single : undefined;
}

function committedValue(workflow: Workflow, field: PortalSectionField): unknown {
  // The facility row is settled by what the collected facilities RESOLVED to, not by the
  // collection itself: a portal string nobody has mapped is collected and still unanswered.
  if (field.name === VALUE_NAMES.FACILITIES) {
    return (workflow.valueFor(MAPPED_FACILITIES_VALUE)?.value ?? undefined) as unknown;
  }
  return (workflow.valueFor(field.name)?.value ?? undefined) as unknown;
}

function targetFor(
  field: PortalSectionField,
  retrieval: WorkflowAction | undefined,
  mapping: WorkflowAction | undefined
): FieldTarget | null {
  if (field.name === VALUE_NAMES.FACILITIES) {
    // Mapping first: while it is outstanding it OWNS the facility answer, and sending
    // `facilities` in the same gesture would hand the run a second, conflicting collection.
    if (mapping) return { kind: 'actionOutputs', actionId: mapping.id, outputName: FACILITY_RESOLUTIONS_OUTPUT };
    if (retrieval) return { kind: 'actionOutputs', actionId: retrieval.id, outputName: VALUE_NAMES.FACILITIES };
    // The facility is settled through mapping, not by typing: with neither action outstanding
    // there is no resolution to submit, and writing `facility_group_ids` by hand would address
    // the referral behind the mapping step's back.
    return null;
  }

  if (retrieval) {
    return { kind: 'actionOutputs', actionId: retrieval.id, outputName: field.composite?.name ?? field.name };
  }
  // Nothing is asking, so the edit is a correction to the run's own value. Composite parts write
  // under their OWN names here — `values` takes value names, and the composite exists only as an
  // action-output shape the api normalizes.
  return { kind: 'workflowValues', valueName: field.name };
}

export function resolveFields(workflow: Workflow): Map<string, FieldState> {
  const retrieval = outstandingRetrieval(workflow);
  const mapping = outstandingFacilityMapping(workflow);
  const unobtainable = unobtainableNames(workflow);

  const fields = REFERRAL_FIELD_SECTIONS.flatMap(portalSectionFields);

  return new Map(
    fields.map((field) => {
      const staged = stagedValue(retrieval, field);
      const committed = staged === undefined ? committedValue(workflow, field) : undefined;
      const value = staged !== undefined ? staged : committed;
      const target = targetFor(field, retrieval, mapping);

      return [
        field.name,
        {
          name: field.name,
          value,
          provenance: staged !== undefined ? 'staged' : committed !== undefined ? 'committed' : 'absent',
          present: isPresent(value) && !unobtainable.has(field.name),
          editable: !!target,
          target,
        } satisfies FieldState,
      ];
    })
  );
}
